import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Re-export for consumers
export { API_BASE_URL };

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    
    if (status === 401) {
      // Don't redirect if already on login page or if this is a login attempt
      const isLoginPage = window.location.pathname === '/login';
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      
      if (!isLoginPage && !isLoginRequest) {
        // Redirect to login on auth failure for protected routes
        window.location.href = '/login';
      }
    }
    
    // Handle multi-org / access validation 403 errors
    if (status === 403 && typeof detail === 'object') {
      const code = detail.code;
      
      if (code === 'org_access_required') {
        // User was removed from org or needs to create one
        if (detail.removed_from_org) {
          sessionStorage.setItem('removed_from_org', detail.removed_from_org.org_name || 'Unknown');
        }
        if (detail.message) {
          sessionStorage.setItem('access_message', detail.message);
        }
        if (detail.available_orgs?.length > 0) {
          sessionStorage.setItem('available_orgs', JSON.stringify(detail.available_orgs));
        }
        
        // Redirect to appropriate page
        const redirect = detail.redirect_to || '/onboarding/create-org';
        if (window.location.pathname !== redirect) {
          window.location.href = redirect;
        }
      }
      
      if (code === 'org_selection_required') {
        // User has multiple orgs and needs to select one
        if (detail.available_orgs) {
          sessionStorage.setItem('available_orgs', JSON.stringify(detail.available_orgs));
        }
        if (window.location.pathname !== '/org-select') {
          window.location.href = '/org-select';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// ============ Products API ============
export const productsApi = {
  list: (includeArchived = false) => api.get(`/api/products?include_archived=${includeArchived}`),
  listArchived: () => api.get('/api/products/archived'),
  get: (productId) => api.get(`/api/products/${productId}`),
  create: (data) => api.post('/api/products', data),
  update: (productId, data) => api.patch(`/api/products/${productId}`, data),
  delete: (productId) => api.delete(`/api/products/${productId}`),
  archive: (productId) => api.post(`/api/products/${productId}/archive`),
  unarchive: (productId) => api.post(`/api/products/${productId}/unarchive`),
  // Kanban column configuration
  getKanbanColumns: (productId) => api.get(`/api/products/${productId}/kanban-columns`),
  updateKanbanColumns: (productId, columns) => api.patch(`/api/products/${productId}/kanban-columns`, { columns }),
  // User-specific preferences
  getUserPreferences: (productId) => api.get(`/api/products/${productId}/user-preferences`),
  updateUserPreferences: (productId, data) => api.put(`/api/products/${productId}/user-preferences`, data),
  // Export
  exportAiContext: (productId) => api.get(`/api/products/${productId}/export/ai-context`, { responseType: 'blob' }),
};

// ============ Personas API ============
export const personasApi = {
  get: (personaId) => api.get(`/api/personas/${personaId}`),
  list: (productId) => api.get(`/api/personas/product/${productId}`),
  listArchived: (productId) => api.get(`/api/personas/product/${productId}/archived`),
  create: (productId, data) => api.post(`/api/personas/product/${productId}`, data),
  update: (personaId, data) => api.patch(`/api/personas/${personaId}`, data),
  delete: (personaId) => api.delete(`/api/personas/${personaId}`),
  archive: (personaId) => api.post(`/api/personas/${personaId}/archive`),
  unarchive: (personaId) => api.post(`/api/personas/${personaId}/unarchive`),
  // NOTE: Conversation logs moved to unifiedCopilotApi.getConversationLogs('persona', personaId)
};

// ============ Unified Co-Pilot API (context-driven) ============
// THE SINGLE API FOR ALL CO-PILOTS
// 
// Context Types:
// - Item types (singular): feature, epic, goal, initiative, persona, release
// - Collection types (plural): features, epics, goals, initiatives, personas, releases, requests, strategy, market, ideation, kanban
// - Workflow types: executive, wizard, collaboration
//
// Context ID:
// - Items: entity_id (e.g., feat_xxx, epic_xxx)
// - Collections: product_id
// - Executive: product_id
// - Wizard: draft_hash
// - Collaboration: space_id
//
export const unifiedCopilotApi = {
  // Chat - THE unified endpoint for ALL co-pilots
  // data can include: message, conversation_history, session_id, canvas_state, attachments, canvas_content, log_type
  // options can include: signal (AbortController signal for cancellation)
  chat: (contextType, contextId, data, options = {}) => 
    api.post(`/api/copilot/${contextType}/${contextId}/chat`, data, { signal: options.signal }),
  
  // Session Management (for co-pilots with persist_sessions=true / user-threads log type)
  listSessions: (contextType = null, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (contextType) params.append('context_type', contextType);
    return api.get(`/api/copilot/sessions?${params}`);
  },
  getSession: (sessionId) => 
    api.get(`/api/copilot/sessions/${sessionId}`),
  renameSession: (sessionId, title) => 
    api.patch(`/api/copilot/sessions/${sessionId}`, { title }),
  deleteSession: (sessionId) => 
    api.delete(`/api/copilot/sessions/${sessionId}`),
  clearSessions: (contextType = null) => {
    const params = contextType ? `?context_type=${contextType}` : '';
    return api.delete(`/api/copilot/sessions${params}`);
  },
  
  // History (per context, user-scoped)
  getHistory: (contextType, contextId, options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    const queryString = params.toString();
    return api.get(`/api/copilot/${contextType}/${contextId}/history${queryString ? `?${queryString}` : ''}`);
  },
  addMessage: (contextType, contextId, role, content, options = {}) => 
    api.post(`/api/copilot/${contextType}/${contextId}/message`, { 
      role, 
      content,
      actions: options.actions
    }),
  clearHistory: (contextType, contextId) => 
    api.delete(`/api/copilot/${contextType}/${contextId}/history`),
  
  // Pending Actions
  savePendingActions: (contextType, contextId, actions) => 
    api.put(`/api/copilot/${contextType}/${contextId}/pending-actions`, actions),
  clearPendingActions: (contextType, contextId) => 
    api.delete(`/api/copilot/${contextType}/${contextId}/pending-actions`),
  
  // Resolve a single action (replace with outcome)
  resolveAction: (contextType, contextId, messageId, actionIndex, outcome, summary) =>
    api.put(`/api/copilot/${contextType}/${contextId}/resolve-action`, {
      message_id: messageId,
      action_index: actionIndex,
      outcome,
      summary
    }),
  
  // Summary
  addSummary: (contextType, contextId, summary, actions = []) => 
    api.post(`/api/copilot/${contextType}/${contextId}/summary`, { summary, actions }),
  
  // Conversation Logs
  getConversationLogs: (contextType, contextId, limit = 10) => 
    api.get(`/api/copilot/${contextType}/${contextId}/conversation-logs?limit=${limit}`),
  
  // Config - fetch copilot settings for a context
  getConfig: (contextType, contextId) => 
    contextId 
      ? api.get(`/api/copilot/${contextType}/${contextId}/config`)
      : api.get(`/api/copilot/${contextType}/config`),
};

// ============ Goals API ============
export const goalsApi = {
  list: (productId) => api.get(`/api/goals/product/${productId}`),
  get: (goalId) => api.get(`/api/goals/${goalId}`),
  getDetails: (goalId) => api.get(`/api/goals/${goalId}/details`),
  create: (productId, data) => api.post(`/api/goals/product/${productId}`, data),
  update: (goalId, data) => api.patch(`/api/goals/${goalId}`, data),
  delete: (goalId) => api.delete(`/api/goals/${goalId}`),
  // Stability check - returns if moving to draft would trigger instability
  checkStabilityImpact: (goalId) => api.get(`/api/goals/${goalId}/stability-check`),
  // KPI Status Settings (projection-based status calculation)
  updateKpiStatusSettings: (goalId, settings) => api.patch(`/api/goals/${goalId}/kpi-status-settings`, settings),
  // Metric time-series logging (Outcome-Driven Model)
  logDataPoint: (goalId, kpiId, data) => api.post(`/api/goals/${goalId}/kpis/${kpiId}/log`, data),
  getMetricHistory: (goalId, kpiId) => api.get(`/api/goals/${goalId}/kpis/${kpiId}/history`),
  deleteDataPoint: (goalId, kpiId, date) => api.delete(`/api/goals/${goalId}/kpis/${kpiId}/log/${date}`),
  // Cascade unlink (hard delete relationships)
  getCascadeImpact: (goalId, initiativeId) => api.get(`/api/goals/${goalId}/cascade-impact/${initiativeId}`),
  executeCascadeUnlink: (goalId, initiativeId) => api.post(`/api/goals/${goalId}/cascade-unlink/${initiativeId}`),
  // Cascade delete KPI (hard delete KPI and all references)
  getKpiCascadeImpact: (goalId, kpiId) => api.get(`/api/goals/${goalId}/kpi/${kpiId}/cascade-impact`),
  executeKpiCascadeDelete: (goalId, kpiId) => api.delete(`/api/goals/${goalId}/kpi/${kpiId}/cascade`),
  // NOTE: Conversation logs moved to unifiedCopilotApi.getConversationLogs('goal', goalId)
};

// ============ Initiatives API ============
export const initiativesApi = {
  list: (goalId) => api.get(`/api/initiatives/goal/${goalId}`),
  listByProduct: (productId) => api.get(`/api/initiatives/product/${productId}`),
  get: (initiativeId) => api.get(`/api/initiatives/${initiativeId}`),
  getProgress: (initiativeId) => api.get(`/api/initiatives/${initiativeId}/progress`),
  getTaggedFeatures: (initiativeId) => api.get(`/api/initiatives/${initiativeId}/tagged-features`),
  create: (goalId, data) => api.post(`/api/initiatives/goal/${goalId}`, data),
  createForProduct: (productId, data) => api.post(`/api/initiatives/product/${productId}`, data),
  update: (initiativeId, data) => api.patch(`/api/initiatives/${initiativeId}`, data),
  delete: (initiativeId) => api.delete(`/api/initiatives/${initiativeId}`),
  // Goal linking (multi-goal support)
  linkGoal: (initiativeId, goalIds) => api.patch(`/api/initiatives/${initiativeId}`, { goal_ids: goalIds }),
  // Metric Targets (Outcome-Driven Model) - LEGACY
  getAvailableMetrics: (productId) => api.get(`/api/initiatives/product/${productId}/available-metrics`),
  getLinkedGoalMetrics: (initiativeId) => api.get(`/api/initiatives/${initiativeId}/available-metrics`),
  addMetricTarget: (initiativeId, target) => api.post(`/api/initiatives/${initiativeId}/metric-targets`, target),
  removeMetricTarget: (initiativeId, targetId) => api.delete(`/api/initiatives/${initiativeId}/metric-targets/${targetId}`),
  // KPI Subscriptions (compound key format "goal_id:kpi_id")
  getInheritedKpis: (initiativeId, queryParams = '') => api.get(`/api/initiatives/${initiativeId}/inherited-kpis${queryParams}`),
  updateSubscribedKpis: (initiativeId, subscribedKpiIds) => api.put(`/api/initiatives/${initiativeId}/subscribed-kpis`, { subscribed_kpi_ids: subscribedKpiIds }),
  subscribeToKpi: (initiativeId, kpiKey) => api.post(`/api/initiatives/${initiativeId}/subscribe-kpi`, { kpi_key: kpiKey }),
  unsubscribeFromKpi: (initiativeId, kpiKey) => api.delete(`/api/initiatives/${initiativeId}/subscribe-kpi/${encodeURIComponent(kpiKey)}`),
  // Follow Type Impact Assessment
  getFollowTypeImpact: (initiativeId, targetType) => 
    api.get(`/api/initiatives/${initiativeId}/follow-type-impact?target_type=${targetType}`),
  // Initiative-owned Tags (for tag-following initiatives)
  getTags: (initiativeId) => api.get(`/api/initiatives/${initiativeId}/tags`),
  createTag: (initiativeId, data) => api.post(`/api/initiatives/${initiativeId}/tags`, data),
  updateTag: (initiativeId, tagId, data) => api.patch(`/api/initiatives/${initiativeId}/tags/${tagId}`, data),
  deleteTag: (initiativeId, tagId, force = false) => 
    api.delete(`/api/initiatives/${initiativeId}/tags/${tagId}?force=${force}`),
};

// ============ Epics API ============
export const epicsApi = {
  get: (epicId) => api.get(`/api/epics/${epicId}`),
  listByInitiative: (initiativeId) => api.get(`/api/epics/initiative/${initiativeId}`),
  listByProduct: (productId, { includeArchived = false } = {}) => 
    api.get(`/api/epics/product/${productId}${includeArchived ? '?include_archived=true' : ''}`),
  createForInitiative: (initiativeId, data) => api.post(`/api/epics/initiative/${initiativeId}`, data),
  createForProduct: (productId, data) => api.post(`/api/epics/product/${productId}`, data),
  update: (epicId, data) => api.patch(`/api/epics/${epicId}`, data),
  delete: (epicId) => api.delete(`/api/epics/${epicId}`),
  audit: (epicId) => api.post(`/api/epics/${epicId}/audit`),
  // Link epic to initiative
  linkToInitiative: (epicId, initiativeId) => api.patch(`/api/epics/${epicId}`, { initiative_id: initiativeId }),
  // Unlink epic from initiative
  unlinkFromInitiative: (epicId) => api.patch(`/api/epics/${epicId}`, { initiative_id: null }),
  // New Epic modal endpoints
  getDetails: (epicId) => api.get(`/api/epics/${epicId}/details`),
  getHealth: (epicId) => api.get(`/api/epics/${epicId}/health`),
  // KPI Subscriptions (compound key format "goal_id:kpi_id")
  getInheritedKpis: (epicId) => api.get(`/api/epics/${epicId}/inherited-kpis`),
  updateSubscribedKpis: (epicId, payload) => api.put(`/api/epics/${epicId}/subscribed-kpis`, payload),
  // Success metrics
  addMetric: (epicId, data) => api.post(`/api/epics/${epicId}/metrics`, data),
  updateMetric: (epicId, metricId, data) => api.patch(`/api/epics/${epicId}/metrics/${metricId}`, data),
  deleteMetric: (epicId, metricId) => api.delete(`/api/epics/${epicId}/metrics/${metricId}`),
  // AI co-pilot - Use copilotApi.chat({ context_type: "epic", context_id: epicId, ... }) instead
  // See /app/docs/COMPLETE_ITEM_CO-PILOT_STACK.md for the correct pattern
  // NOTE: Conversation logs moved to unifiedCopilotApi.getConversationLogs('epic', epicId)
};

// ============ Features API ============
// Hierarchy: Product -> Epic -> Feature -> Requirements (embedded)
export const featuresApi = {
  list: (epicId) => api.get(`/api/features/epic/${epicId}`),
  listByProduct: (productId) => api.get(`/api/features/product/${productId}`),
  listArchived: (productId) => api.get(`/api/features/product/${productId}/archived`),
  get: (featureId) => api.get(`/api/features/${featureId}`),
  create: (epicId, data) => api.post(`/api/features/epic/${epicId}`, data),
  createForProduct: (productId, data) => api.post(`/api/features/product/${productId}`, data),
  update: (featureId, data) => api.patch(`/api/features/${featureId}`, data),
  delete: (featureId) => api.delete(`/api/features/${featureId}`),
  restore: (featureId) => api.post(`/api/features/${featureId}/restore`),
  // Split a feature into two
  split: (featureId, splitData) => api.post(`/api/features/${featureId}/split`, splitData),
  // Requirements (acceptance criteria) within features
  addRequirement: (featureId, data) => api.post(`/api/features/${featureId}/requirements`, data),
  updateRequirement: (featureId, requirementId, data) => api.patch(`/api/features/${featureId}/requirements/${requirementId}`, data),
  deleteRequirement: (featureId, requirementId) => api.delete(`/api/features/${featureId}/requirements/${requirementId}`),
  // KPI Alignments (compound key format with hypothesis)
  getInheritedKpis: (featureId) => api.get(`/api/features/${featureId}/inherited-kpis`),
  updateKpiAlignments: (featureId, payload) => api.put(`/api/features/${featureId}/kpi-alignments`, payload),
  // Usage stats for free tier limits
  getUsage: () => api.get('/api/features/usage'),
  // Migration helper
  migratePriorityRanks: () => api.post('/api/features/migrate/priority-ranks'),
  // NOTE: Conversation logs moved to unifiedCopilotApi.getConversationLogs('feature', featureId)
  // Attachments
  getAttachments: (featureId) => api.get(`/api/features/${featureId}/attachments`),
  uploadAttachment: (featureId, formData) => api.post(`/api/features/${featureId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAttachment: (featureId, attachmentId) => api.delete(`/api/features/${featureId}/attachments/${attachmentId}`),
  // Mark feature as viewed (clears NEW badge for split features)
  markViewed: (featureId) => api.post(`/api/features/${featureId}/mark-viewed`),
  // Dev Log - MCP Activity for feature
  getActivity: (featureId, params = {}) => api.get(`/api/features/${featureId}/activity`, { params }),
};

// Backwards compatibility alias
export const requirementsApi = featuresApi;

// ============ Tags API (Organization-scoped - legacy) ============
export const tagsApi = {
  list: () => api.get('/api/tags'),
  search: (query) => api.get(`/api/tags/search?q=${encodeURIComponent(query)}`),
  create: (data) => api.post('/api/tags', data),
  update: (tagId, data) => api.patch(`/api/tags/${tagId}`, data),
  delete: (tagId) => api.delete(`/api/tags/${tagId}`),
};

// ============ Product Tags API (Product-scoped) ============
export const productTagsApi = {
  // List all tags for a product (excludes initiative tags by default)
  list: (productId, includeInitiativeTags = false) => 
    api.get(`/api/products/${productId}/tags?include_initiative_tags=${includeInitiativeTags}`),
  
  // List initiative tags for a product (for Feature's "Initiative Tags" tab)
  listInitiativeTags: (productId, initiativeId = null) => {
    const params = initiativeId ? `?initiative_id=${initiativeId}` : '';
    return api.get(`/api/products/${productId}/tags/initiative-tags${params}`);
  },
  
  // Search/autocomplete tags
  search: (productId, query) => api.get(`/api/products/${productId}/tags/search?q=${encodeURIComponent(query || '')}`),
  
  // Create a new tag
  create: (productId, data) => api.post(`/api/products/${productId}/tags`, data),
  
  // Update tag name or color
  update: (productId, tagId, data) => api.patch(`/api/products/${productId}/tags/${tagId}`, data),
  
  // Delete tag (force=true to remove from all features)
  delete: (productId, tagId, force = false) => api.delete(`/api/products/${productId}/tags/${tagId}?force=${force}`),
  
  // Merge multiple tags into one
  merge: (productId, data) => api.post(`/api/products/${productId}/tags/merge`, data),
  
  // Get color palette and used colors
  getColors: (productId) => api.get(`/api/products/${productId}/tags/colors`),
};

// ============ AI Co-Pilot API ============
// LEGACY WRAPPER - Routes through unified endpoint
export const copilotApi = {
  // Chat - NOW USES UNIFIED ENDPOINT
  chat: (data) => unifiedCopilotApi.chat(data.context_type, data.context_id, {
    message: data.message,
    conversation_history: data.conversation_history
  }),
  extractProduct: (data) => api.post('/api/copilot/extract-product', data),
  extractProductWithFiles: (formData) => api.post('/api/copilot/extract-product-with-files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  generatePersonas: (data) => api.post('/api/copilot/generate-personas', data),
  // Conversation summarization
  summarizeConversation: (data) => api.post('/api/copilot/summarize-conversation', data),
};

// ============ Unified Collection Co-pilot API (DEPRECATED) ============
// DEPRECATED: Use unifiedCopilotApi.chat() instead
// Kept for backwards compatibility
export const unifiedCollectionCopilotApi = {
  // Chat with collection co-pilot using two-model flow
  // contextType: features, epics, goals, initiatives, personas, requests, releases
  // contextId: product_id (collections are product-scoped)
  chat: (contextType, contextId, data) => 
    api.post(`/api/copilot/${contextType}/${contextId}/chat`, data),
};

// ============ Collection Actions API ============
export const collectionActionsApi = {
  createItem: (data) => api.post('/api/collection-actions/create-item', data),
  updateItem: (data) => api.post('/api/collection-actions/update-item', data),
  linkItems: (data) => api.post('/api/collection-actions/link-items', data),
  unlinkItems: (data) => api.post('/api/collection-actions/unlink-items', data),
  deleteItem: (data) => api.post('/api/collection-actions/delete-item', data),
  bulkUpdate: (data) => api.post('/api/collection-actions/bulk-update', data),
  bulkCreate: (data) => api.post('/api/collection-actions/bulk-create', data),
  bulkLink: (data) => api.post('/api/collection-actions/bulk-link', data),
  mergeItems: (data) => api.post('/api/collection-actions/merge-items', data),
  // First-class embedded entity actions (e.g., market_segment in product.market_segments)
  createEmbeddedEntity: (data) => api.post('/api/collection-actions/create-embedded-entity', data),
  updateEmbeddedEntity: (data) => api.post('/api/collection-actions/update-embedded-entity', data),
  deleteEmbeddedEntity: (data) => api.post('/api/collection-actions/delete-embedded-entity', data),
  // Fetch original values for UPDATE comparison
  getEmbeddedEntity: (parentId, itemId, entityType) => 
    api.get(`/api/collection-actions/get-embedded-entity?parent_id=${parentId}&item_id=${itemId}&entity_type=${entityType}`),
  getEmbeddedEntitiesBatch: (items) => 
    api.post('/api/collection-actions/get-embedded-entities-batch', { items }),
  // Fetch standard entities for UPDATE_ITEM comparison
  getStandardEntitiesBatch: (items) => 
    api.post('/api/collection-actions/get-standard-entities-batch', { items }),
  // Get field schema for an entity type (required fields, types, labels)
  getFieldSchema: (entityType) =>
    api.get(`/api/collection-actions/field-schema/${entityType}`),
};

// ============ Infrastructure API ============
export const infrastructureApi = {
  // Get infrastructure status
  getStatus: (productId) => api.get(`/api/products/${productId}/infrastructure`),
  
  // Get version history
  getVersionHistory: (productId) => api.get(`/api/products/${productId}/infrastructure/versions`),
  
  // Set bootstrap mode (without creating task yet)
  setBootstrapMode: (productId) => api.post(`/api/products/${productId}/infrastructure/bootstrap-mode`),
  
  // Create bootstrap task (legacy - use createArchitectureFeature instead)
  createBootstrap: (productId, data) => api.post(`/api/products/${productId}/infrastructure/bootstrap`, data),
  
  // Create architecture feature (bootstrap or update)
  createArchitectureFeature: (productId, data) => api.post(`/api/products/${productId}/infrastructure/build`, data),
  
  // Publish constraints (minor version increment)
  publishConstraints: (productId, data) => api.post(`/api/products/${productId}/infrastructure/publish`, data),
  
  // Cancel in-progress build
  cancelBuild: (productId) => api.post(`/api/products/${productId}/infrastructure/cancel`),
  
  // Update a 'not_started' build (full config update)
  updateBuild: (productId, data) => api.put(`/api/products/${productId}/infrastructure/build`, data),
  
  // Change build status (move between columns)
  changeBuildStatus: (productId, data) => api.patch(`/api/products/${productId}/infrastructure/build/status`, data),
  
  // Dismiss a completed build from kanban
  dismissBuild: (productId) => api.post(`/api/products/${productId}/infrastructure/build/dismiss`),
  
  // Set existing infrastructure mode
  setExisting: (productId) => api.post(`/api/products/${productId}/infrastructure/existing`),
  
  // Update infrastructure status
  updateStatus: (productId, data) => api.patch(`/api/products/${productId}/infrastructure`, data),
  
  // Trigger infrastructure update
  triggerUpdate: (productId, data) => api.post(`/api/products/${productId}/infrastructure/trigger-update`, data),
  
  // Get activity log
  getActivity: (productId, limit = 50) => api.get(`/api/products/${productId}/infrastructure/activity?limit=${limit}`),
  
  // Get team members with MCP keys
  getMcpKeyHolders: (productId) => api.get(`/api/products/${productId}/infrastructure/mcp-key-holders`),
};

// ============ Team API ============
export const teamApi = {
  list: () => api.get('/api/team'),
  getMyMembership: () => api.get('/api/team/me'),
  invite: (data) => api.post('/api/team/invite', data),
  updateRole: (memberId, role) => api.patch(`/api/team/${memberId}/role`, { role }),
  remove: (memberId) => api.delete(`/api/team/${memberId}`),
};

// ============ Organizations API ============
export const organizationsApi = {
  // Get current org
  getMyOrg: () => api.get('/api/organizations/me'),
  updateMyOrg: (data) => api.patch('/api/organizations/me', data),
  
  // Organization closure
  getClosureStatus: () => api.get('/api/organizations/me/closure-status'),
  closeOrganization: (confirmName, cancelImmediately = false) => 
    api.post('/api/organizations/me/close', { 
      confirm_name: confirmName, 
      cancel_subscription_immediately: cancelImmediately 
    }),
  reactivateOrganization: () => api.post('/api/organizations/me/reactivate'),
  exportData: () => api.get('/api/organizations/me/export'),
  
  // Navigation settings
  applyNavigationToProducts: (productIds, navigationSettings) =>
    api.post('/api/organizations/me/apply-navigation-to-products', {
      product_ids: productIds,
      navigation_settings: navigationSettings
    }),
};

// ============ Organization Integrations API ============
export const orgIntegrationsApi = {
  // Get all integrations
  getIntegrations: () => api.get('/api/organizations/integrations'),
  
  // Get available platforms
  getAvailablePlatforms: () => api.get('/api/organizations/integrations/available-platforms'),
  
  // GitHub
  connectGitHub: (data) => api.post('/api/organizations/integrations/github/connect', data),
  syncGitHubRepos: () => api.post('/api/organizations/integrations/github/sync-repos'),
  updateGitHubRepos: (repos) => api.put('/api/organizations/integrations/github/repos', repos),
  disconnectGitHub: () => api.delete('/api/organizations/integrations/github'),
  
  // JIRA
  disconnectJira: () => api.delete('/api/organizations/integrations/jira'),
  
  // Linear
  disconnectLinear: () => api.delete('/api/organizations/integrations/linear'),
};

// ============ Product Integrations API ============
export const productIntegrationsApi = {
  // Get all integrations for a product
  getIntegrations: (productId) => api.get(`/api/products/${productId}/integrations`),
  
  // Get available repos from org's GitHub
  getAvailableRepos: (productId) => api.get(`/api/products/${productId}/integrations/available-repos`),
  
  // Get available ticketing options
  getAvailableTicketing: (productId) => api.get(`/api/products/${productId}/integrations/available-ticketing`),
  
  // Update MCP
  updateMCP: (productId, data) => api.put(`/api/products/${productId}/integrations/mcp`, data),
  
  // Update source control
  updateSourceControl: (productId, data) => api.put(`/api/products/${productId}/integrations/source-control`, data),
  
  // Update ticketing
  updateTicketing: (productId, data) => api.put(`/api/products/${productId}/integrations/ticketing`, data),
  
  // Update automation
  updateAutomation: (productId, data) => api.put(`/api/products/${productId}/integrations/automation`, data),
};

// ============ GitHub App API ============
export const githubAppApi = {
  // Get setup info (callback URL, webhook URL)
  getSetupInfo: () => api.get('/api/integrations/github/setup-info'),
  
  // List installations (admin only)
  listInstallations: () => api.get('/api/integrations/github/installations'),
  
  // Link installation to org
  linkInstallation: (installationId) => api.post(`/api/integrations/github/link-installation?installation_id=${installationId}`),
  
  // Sync repos
  syncRepos: () => api.post('/api/integrations/github/sync-repos'),
};

// ============ Releases API ============
export const releasesApi = {
  // CRUD
  list: (productId) => api.get(`/api/releases/product/${productId}`),
  get: (releaseId) => api.get(`/api/releases/${releaseId}`),
  create: (productId, data) => api.post(`/api/releases/product/${productId}`, data),
  update: (releaseId, data) => api.patch(`/api/releases/${releaseId}`, data),
  delete: (releaseId) => api.delete(`/api/releases/${releaseId}`),
  
  // Sprint settings
  getSprintSettings: (productId) => api.get(`/api/releases/product/${productId}/sprint-settings`),
  updateSprintSettings: (productId, data) => api.patch(`/api/releases/product/${productId}/sprint-settings`, data),
  recalculateDates: (productId) => api.post(`/api/releases/product/${productId}/recalculate-dates`),
  
  // Two-Stage Release Process
  // Stage 0: Start Sprint
  start: (releaseId) => api.post(`/api/releases/${releaseId}/start`),
  
  // Stage 1: Close Sprint
  close: (releaseId, moveToNext = true) => api.post(`/api/releases/${releaseId}/close`, { move_incomplete_to_next: moveToNext }),
  closePreview: (releaseId) => api.get(`/api/releases/${releaseId}/close-preview`),
  
  // Stage 2: Promote to Production
  promoteToProduction: (releaseId, featureIds) => api.post(`/api/releases/${releaseId}/promote-to-production`, { feature_ids: featureIds }),
  // Promote single feature to production (for CD mode)
  promoteSingleFeature: (featureId) => api.post(`/api/releases/feature/${featureId}/promote-to-production`),
  productionPreview: (releaseId) => api.get(`/api/releases/${releaseId}/production-preview`),
  
  // Feature assignment
  assignFeature: (releaseId, featureId) => api.post(`/api/releases/${releaseId}/features/${featureId}`),
  unassignFeature: (releaseId, featureId) => api.delete(`/api/releases/${releaseId}/features/${featureId}`),
  getFeatures: (releaseId) => api.get(`/api/releases/${releaseId}/features`),
  
  // External ticket management (provider-agnostic: GitHub, JIRA, Linear, etc.)
  pushTicket: (featureId) => api.post(`/api/releases/features/${featureId}/push-ticket`),
  // Legacy alias for backwards compatibility
  retryGitHubIssue: (featureId) => api.post(`/api/releases/features/${featureId}/push-ticket`),
  
  // Helpers
  getDefaultName: (productId) => api.get(`/api/releases/product/${productId}/default-name`),
};

// ============ Auth API ============
export const authApi = {
  me: () => api.get('/api/auth/me'),
  createSession: (sessionId) => api.post('/api/auth/session', { session_id: sessionId }),
  logout: () => api.post('/api/auth/logout'),
  
  /**
   * Complete OAuth signup after user selects a plan
   * Used when user tries to sign in with OAuth but doesn't have an account
   * @param {string} oauthToken - Signed JWT from OAuth callback
   * @param {string} planId - Selected plan ID
   * @param {string} gridVersionId - Pricing grid version ID
   * @param {string} abVariant - A/B test variant
   */
  completeOAuthSignup: (oauthToken, planId, gridVersionId, abVariant) => 
    api.post('/api/auth/complete-oauth-signup', {
      oauth_token: oauthToken,
      plan_id: planId,
      grid_version_id: gridVersionId,
      ab_variant: abVariant
    }),
};

// ============ Utility Functions ============

// Fetch full product hierarchy (for ProductDetailPage)
// New hierarchy: Product -> Epic -> Feature -> Requirements (embedded)
export const fetchProductHierarchy = async (productId) => {
  const [productRes, personasRes, goalsRes, epicsRes] = await Promise.all([
    productsApi.get(productId),
    personasApi.list(productId).catch(() => ({ data: [] })),  // Handle 403 for collaborators
    goalsApi.list(productId).catch(() => ({ data: [] })),     // Handle 403 for collaborators
    epicsApi.listByProduct(productId).catch(() => ({ data: [] })),
  ]);

  const product = productRes.data;
  const personas = personasRes.data;
  const goals = goalsRes.data;
  const epics = epicsRes.data;

  // Fetch features for each epic (new hierarchy)
  const epicsWithFeatures = await Promise.all(
    epics.map(async (epic) => {
      const featuresRes = await featuresApi.list(epic.epic_id);
      return {
        ...epic,
        features: featuresRes.data,
      };
    })
  );

  // Legacy: Fetch initiatives for each goal (for backwards compatibility)
  const goalsWithInitiatives = await Promise.all(
    goals.map(async (goal) => {
      const initiativesRes = await initiativesApi.list(goal.goal_id);
      const initiatives = initiativesRes.data;

      // Fetch epics for each initiative (legacy path)
      const initiativesWithEpics = await Promise.all(
        initiatives.map(async (initiative) => {
          const epicsRes = await epicsApi.listByInitiative(initiative.initiative_id);
          const epics = epicsRes.data;

          // Fetch features for each epic
          const epicsWithFeatures = await Promise.all(
            epics.map(async (epic) => {
              const featuresRes = await featuresApi.list(epic.epic_id);
              return {
                ...epic,
                features: featuresRes.data,
              };
            })
          );

          return {
            ...initiative,
            epics: epicsWithFeatures,
          };
        })
      );

      return {
        ...goal,
        initiatives: initiativesWithEpics,
      };
    })
  );

  return {
    product,
    personas,
    goals: goalsWithInitiatives,
    epics: epicsWithFeatures, // Direct product -> epic -> feature path
  };
};

// Count stats for dashboard
// Updated for new hierarchy: Product -> Epic -> Feature -> Requirements
export const fetchDashboardStats = async () => {
  const productsRes = await productsApi.list();
  const products = productsRes.data;

  let totalFeatures = 0;
  let inProgress = 0;
  let completed = 0;

  // For each product, count features from direct epics
  for (const product of products) {
    try {
      const epicsRes = await epicsApi.listByProduct(product.product_id);
      for (const epic of epicsRes.data) {
        const featuresRes = await featuresApi.list(epic.epic_id);
        for (const feature of featuresRes.data) {
          totalFeatures++;
          if (feature.status === 'In Progress') inProgress++;
          if (feature.status === 'Done') completed++;
        }
      }
    } catch (e) {
      console.error('Error fetching stats for product:', product.product_id, e);
    }
  }

  return {
    products: products.length,
    totalFeatures,
    totalRequirements: totalFeatures, // Backwards compatibility
    inProgress,
    completed,
    productsList: products,
  };
};

// ============ Notifications API ============
export const notificationsApi = {
  // Get notifications list
  list: (params = {}) => api.get('/api/notifications', { params }),
  
  // Get unread count
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  
  // Mark single notification as read
  markAsRead: (notificationId) => api.patch(`/api/notifications/${notificationId}/read`),
  
  // Mark all as read
  markAllAsRead: () => api.post('/api/notifications/mark-all-read'),
  
  // Delete notification
  delete: (notificationId) => api.delete(`/api/notifications/${notificationId}`),
  
  // Clear all notifications
  clearAll: () => api.delete('/api/notifications'),
  
  // Get notification preferences
  getPreferences: () => api.get('/api/notifications/preferences'),
  
  // Update notification preferences
  updatePreferences: (preferences) => api.put('/api/notifications/preferences', preferences),
  
  // Create test notification (dev only)
  createTest: () => api.post('/api/notifications/test'),
};

// ============ Executive Co-Pilot API ============
// MIGRATED TO UNIFIED API - These now route through /api/copilot/executive/{product_id}/chat
export const executiveCopilotApi = {
  // Chat - NOW USES UNIFIED ENDPOINT
  chat: (message, sessionId = null, scope = 'all') => 
    unifiedCopilotApi.chat('executive', scope, { message, session_id: sessionId }),
  
  // Sessions - NOW USES UNIFIED SESSION MANAGEMENT
  listSessions: (limit = 50, offset = 0) => 
    unifiedCopilotApi.listSessions('executive', limit, offset),
  
  // Get single session with messages
  getSession: (sessionId) => 
    unifiedCopilotApi.getSession(sessionId),
  
  // Rename session
  renameSession: (sessionId, title) => 
    unifiedCopilotApi.renameSession(sessionId, title),
  
  // Delete session
  deleteSession: (sessionId) => 
    unifiedCopilotApi.deleteSession(sessionId),
  
  // Clear ALL sessions for current user
  clearAllSessions: () => 
    unifiedCopilotApi.clearSessions('executive'),
  
  // LEGACY - These still use the old endpoints until migrated
  getMetrics: (scope = 'all') => 
    api.get('/api/executive-copilot/metrics', { params: { scope } }),
  getWelcome: (scope = 'all') => 
    api.get('/api/executive-copilot/welcome', { params: { scope } }),
  listProducts: () => 
    api.get('/api/executive-copilot/products'),
  resolveEntity: (entityType, entityId) =>
    api.get(`/api/executive-copilot/resolve-entity/${entityType}/${entityId}`),
};

// ============ User Settings API ============
export const userSettingsApi = {
  get: () => api.get('/api/user/settings'),
  update: (settings) => api.patch('/api/user/settings', settings),
  getNotificationPreferences: () => api.get('/api/user/settings/notification-preferences'),
};

// ============ Kanban Column Rules API ============
export const kanbanRulesApi = {
  get: (productId) => api.get(`/api/products/${productId}/kanban-column-rules`),
  update: (productId, rules) => api.patch(`/api/products/${productId}/kanban-column-rules`, { rules }),
};

// ============ Unified Notes API ============
// Polymorphic notes system for any entity type: feature, goal, epic, initiative
export const unifiedNotesApi = {
  list: (entityType, entityId) => api.get('/api/notes', { params: { entity_type: entityType, entity_id: entityId } }),
  create: (data) => api.post('/api/notes', data),  // data = { entity_type, entity_id, content, reply_to? }
  update: (noteId, data) => api.put(`/api/notes/${noteId}`, data),  // data = { content }
  delete: (noteId) => api.delete(`/api/notes/${noteId}`),
  updateReadPosition: (entityType, entityId, lastReadNoteId) => api.put('/api/notes/read-position', { 
    entity_type: entityType, 
    entity_id: entityId, 
    last_read_note_id: lastReadNoteId 
  }),
  getUnreadCount: (entityType, entityId) => api.get('/api/notes/unread-count', { 
    params: { entity_type: entityType, entity_id: entityId } 
  }),
};

// NOTE: Legacy notesApi has been REMOVED - use unifiedNotesApi instead
// NOTE: chatHistoryApi and productCopilotHistoryApi have been REMOVED - use unifiedCopilotApi instead

// NOTE: subscriptionApi has been REMOVED - use billingApi instead
// - getLimits() -> billingApi.getLimits()
// - updateBilling() -> billingApi.updateBillingInfo()

// ============ Stripe Billing API ============
// For Stripe-specific operations: checkout, portal, invoices, seats
export const stripeApi = {
  // Get Stripe configuration
  getConfig: () => api.get('/api/stripe/config'),
  
  // Get subscription status (includes cancel_at_period_end and subscription_ends_at)
  getSubscription: () => api.get('/api/stripe/subscription'),
  
  // Get seat pool status (seats_purchased, seats_occupied, seats_available)
  getSeatStatus: () => api.get('/api/stripe/seats'),
  
  // Add additional seats to existing subscription
  addSeats: (seats) => api.post('/api/stripe/seats/add', { seats }),
  
  // Create checkout session for subscription
  // Supports: { seats, plan_id, success_url, cancel_url }
  createCheckoutSession: (params) => {
    // Require params to be an object with plan_id
    if (typeof params !== 'object' || !params.plan_id) {
      throw new Error('createCheckoutSession requires {plan_id, seats?, success_url?, cancel_url?}');
    }
    return api.post('/api/stripe/create-checkout-session', {
      seats: params.seats || 1,
      plan_id: params.plan_id,
      success_url: params.success_url || null,
      cancel_url: params.cancel_url || null
    });
  },
  
  // Create customer portal session
  createPortalSession: (returnUrl = null) => api.post('/api/stripe/create-portal-session', { return_url: returnUrl }),
  
  // Finalize free→paid upgrade (create Goal + Initiative scaffolding)
  finalizeUpgrade: () => api.post('/api/stripe/finalize-free-upgrade'),
  
  // Update seat count
  updateSeats: (seats) => api.post('/api/stripe/update-seats', { seats }),
  
  // Manually sync subscription from Stripe (fallback if webhook doesn't fire)
  syncSubscription: () => api.post('/api/stripe/sync-subscription'),
  
  // Get billing history (invoices)
  getInvoices: () => api.get('/api/stripe/invoices'),
  
  // Get payment methods
  getPaymentMethods: () => api.get('/api/stripe/payment-methods'),
  
  // Set default payment method
  setDefaultPaymentMethod: (paymentMethodId) => 
    api.post('/api/stripe/payment-methods/default', { payment_method_id: paymentMethodId }),
  
  // Create SetupIntent for adding new payment method
  createSetupIntent: () => api.post('/api/stripe/payment-methods/setup-intent'),
  
  // Delete payment method
  deletePaymentMethod: (paymentMethodId) => 
    api.delete(`/api/stripe/payment-methods/${paymentMethodId}`),
  
  // Get Stripe publishable key
  getPublishableKey: () => api.get('/api/stripe/publishable-key'),
  
  // Get upcoming invoice preview
  getUpcomingInvoice: () => api.get('/api/stripe/upcoming-invoice'),
};

// ============ Billing Context API ============
// Consolidated billing API for upgrade flows, limits, and billing info
export const billingApi = {
  // === THE UNIFIED CONTEXT ENDPOINT ===
  // This is the SINGLE SOURCE OF TRUTH - returns everything in one call
  getContext: () => api.get('/api/billing/context'),
  
  // === Available Plans for Billing Grid ===
  getAvailablePlans: () => api.get('/api/billing/available-plans'),
  
  // === Limits & Usage (consolidated from subscriptionApi) ===
  getLimits: () => api.get('/api/billing/limits'),
  getStatus: () => api.get('/api/subscription/status'),  // Organization subscription status
  updateBillingInfo: (data) => api.patch('/api/billing/info', data),
  
  // === Upgrade Context ===
  // Get upgrade context (role, payment method, limits, options)
  getUpgradeContext: (limitType = 'products') => 
    api.get(`/api/billing/upgrade-context?limit_type=${limitType}`),
  
  // One-click upgrade for owners with payment method on file
  oneClickUpgrade: (planId, returnUrl = null) => 
    api.post('/api/billing/one-click-upgrade', { plan_id: planId, return_url: returnUrl }),
  
  // Cancel subscription (downgrade to free)
  cancelSubscription: () => api.post('/api/subscription/downgrade'),
  
  // Request upgrade from owner (for PMs/members)
  requestUpgrade: (limitType, message = null) => 
    api.post('/api/billing/request-upgrade', { limit_type: limitType, message }),
  
  // Get pending upgrade requests (for banner display) - supports multiple
  getPendingUpgradeRequests: () => api.get('/api/billing/pending-upgrade-requests'),
  
  // Legacy: Get single pending upgrade request (for banner display)
  getPendingUpgradeRequest: () => api.get('/api/billing/pending-upgrade-request'),
  
  // Dismiss upgrade request(s)
  dismissUpgradeRequest: (requestId = null) => 
    api.post('/api/billing/dismiss-upgrade-request', requestId ? { request_id: requestId } : {}),
  
  // Dismiss all upgrade requests
  dismissAllUpgradeRequests: () => api.post('/api/billing/dismiss-all-upgrade-requests'),
  
  // === Requester (PM/Collaborator) APIs ===
  
  // Get my own upgrade request status
  getMyUpgradeRequest: () => api.get('/api/billing/my-upgrade-request'),
  
  // Send reminder to owner
  sendUpgradeReminder: () => api.post('/api/billing/send-upgrade-reminder'),
  
  // Dismiss/cancel my own request
  dismissMyUpgradeRequest: () => api.post('/api/billing/dismiss-my-upgrade-request'),
  
  // Re-request after expiry
  reRequestUpgrade: () => api.post('/api/billing/re-request-upgrade'),
};

// ============ Site Analytics API ============
export const analyticsApi = {
  // Site analytics (landing page / public pages)
  getSiteAnalytics: (days = 30) => api.get(`/api/analytics/site?days=${days}`),
  
  // App analytics (logged-in user activity)
  getAppAnalytics: (days = 30) => api.get(`/api/analytics/app?days=${days}`),
  
  // App health metrics (AI health, action quality, system errors)
  getHealthMetrics: (days = 30) => api.get(`/api/admin/metrics/app-health?days=${days}`),
};

// ============ Admin Testing API ============
export const adminTestingApi = {
  // Organizations
  listOrganizations: () => api.get('/api/admin/testing/organizations'),
  createOrganization: (data) => api.post('/api/admin/testing/organizations', data),
  updateOrganization: (orgId, data) => api.patch(`/api/admin/testing/organizations/${orgId}`, data),
  deleteOrganization: (orgId) => api.delete(`/api/admin/testing/organizations/${orgId}`),
  
  // Plan Management
  changePlan: (orgId, planData) => api.post(`/api/admin/testing/organizations/${orgId}/change-plan`, planData),
  getPricingGrid: (orgId) => api.get(`/api/admin/testing/organizations/${orgId}/pricing-grid`),
  
  // Team Members
  listTeamMembers: (organizationId = null) => 
    api.get(`/api/admin/testing/team-members${organizationId ? `?organization_id=${organizationId}` : ''}`),
  createTeamMember: (data) => api.post('/api/admin/testing/team-members', data),
  updateTeamMember: (memberId, data) => api.patch(`/api/admin/testing/team-members/${memberId}`, data),
  deleteTeamMember: (memberId) => api.delete(`/api/admin/testing/team-members/${memberId}`),
  
  // Data Management
  clearData: (data) => api.post('/api/admin/testing/clear-data', data),
  getStats: () => api.get('/api/admin/testing/stats'),
};

// ============ Collaboration Spaces API ============
export const collaborationApi = {
  // Spaces CRUD
  listSpaces: (productId) => api.get(`/api/collaboration-spaces/product/${productId}`),
  createSpace: (productId, data) => api.post(`/api/collaboration-spaces/product/${productId}`, data),
  getSpace: (spaceId) => api.get(`/api/collaboration-spaces/${spaceId}`),
  updateSpace: (spaceId, data) => api.patch(`/api/collaboration-spaces/${spaceId}`, data),
  deleteSpace: (spaceId) => api.delete(`/api/collaboration-spaces/${spaceId}`),
  
  // Collaborators
  listCollaborators: (spaceId) => api.get(`/api/collaboration-spaces/${spaceId}/collaborators`),
  inviteCollaborator: (spaceId, email, role = 'edit') => api.post(`/api/collaboration-spaces/${spaceId}/invite`, { email, role }),
  removeCollaborator: (spaceId, email) => api.delete(`/api/collaboration-spaces/${spaceId}/collaborators/${encodeURIComponent(email)}`),
  
  // Invites
  getInviteInfo: (token) => axios.get(`${API_BASE_URL}/api/collaboration-spaces/invite/${token}`),
  acceptInvite: (token) => api.post(`/api/collaboration-spaces/invite/${token}/accept`),
  claimInvite: (token, data) => axios.post(`${API_BASE_URL}/api/collaboration-spaces/invite/${token}/claim`, data),
  
  // Share Settings & Public Links
  getShareSettings: (spaceId) => api.get(`/api/collaboration-spaces/${spaceId}/share-settings`),
  updateShareSettings: (spaceId, data) => api.put(`/api/collaboration-spaces/${spaceId}/share-settings`, data),
  regenerateLink: (spaceId) => api.post(`/api/collaboration-spaces/${spaceId}/regenerate-link`),
  
  // Public Link / Guest Access (no auth required)
  getPublicSpaceInfo: (token) => axios.get(`${API_BASE_URL}/api/collaboration-spaces/join/${token}`),
  joinAsGuest: (token, data) => axios.post(`${API_BASE_URL}/api/collaboration-spaces/join/${token}`, data),
  validateGuestSession: (sessionToken) => axios.get(`${API_BASE_URL}/api/collaboration-spaces/guest-session/${sessionToken}`),
  
  // Guest Space & Elements (no auth required)
  guestGetSpace: (sessionToken) => axios.get(`${API_BASE_URL}/api/collaboration-spaces/guest/${sessionToken}/space`),
  guestListElements: (sessionToken, spaceId) => axios.get(`${API_BASE_URL}/api/collaboration-spaces/guest/${sessionToken}/elements/${spaceId}`),
  guestCreateElement: (spaceId, sessionToken, data) => axios.post(`${API_BASE_URL}/api/collaboration-spaces/guest/${spaceId}/elements`, { session_token: sessionToken, ...data }),
  guestUpdateElement: (spaceId, elementId, sessionToken, data) => axios.patch(`${API_BASE_URL}/api/collaboration-spaces/guest/${spaceId}/elements/${elementId}`, { session_token: sessionToken, ...data }),
  guestDeleteElement: (spaceId, elementId, sessionToken) => axios.delete(`${API_BASE_URL}/api/collaboration-spaces/guest/${spaceId}/elements/${elementId}?session_token=${sessionToken}`),
  
  // Guest Management
  listGuests: (spaceId) => api.get(`/api/collaboration-spaces/${spaceId}/guests`),
  removeGuest: (spaceId, guestId) => api.delete(`/api/collaboration-spaces/${spaceId}/guests/${guestId}`),
  
  // Elements CRUD
  listElements: (spaceId) => api.get(`/api/collaboration-spaces/${spaceId}/elements`),
  createElement: (spaceId, data) => api.post(`/api/collaboration-spaces/${spaceId}/elements`, data),
  updateElement: (spaceId, elementId, data) => api.patch(`/api/collaboration-spaces/${spaceId}/elements/${elementId}`, data),
  deleteElement: (spaceId, elementId) => api.delete(`/api/collaboration-spaces/${spaceId}/elements/${elementId}`),
  
  // Reactions
  addReaction: (spaceId, elementId, emoji, userId) => 
    api.post(`/api/collaboration-spaces/${spaceId}/elements/${elementId}/reactions`, { emoji, user_id: userId }),
  removeReaction: (spaceId, elementId, emoji) => 
    api.delete(`/api/collaboration-spaces/${spaceId}/elements/${elementId}/reactions/${encodeURIComponent(emoji)}`),
  
  // AI Co-Pilot - Uses unified copilot API
  // History: unifiedCopilotApi.getHistory('collaboration', spaceId)
  // Chat: unifiedCopilotApi.chat('collaboration', spaceId, {...})
  sendAIMessage: (spaceId, data) => unifiedCopilotApi.chat('collaboration', spaceId, {
    message: data.message,
    canvas_content: data.canvas_content,
    conversation_history: data.conversation_history
  }),
  getCopilotConfig: () => api.get('/api/collaboration-spaces/ai/config'),
};

// ============ Delivery Config API ============
export const deliveryConfigApi = {
  // Get full delivery config for a product
  getConfig: (productId) => api.get(`/api/products/${productId}/delivery-config`),
  
  // Get options for UI dropdowns
  getDNAOptions: (productId) => api.get(`/api/products/${productId}/delivery-config/options/dna`),
  getEnvironmentOptions: (productId) => api.get(`/api/products/${productId}/delivery-config/options/environment`),
  getPresets: (productId) => api.get(`/api/products/${productId}/delivery-config/options/presets`),
  getCloudRegions: (productId) => api.get(`/api/products/${productId}/delivery-config/options/regions`),
  
  // Update settings
  updateDNA: (productId, data) => api.put(`/api/products/${productId}/delivery-config/dna`, data),
  updateEnvironment: (productId, data) => api.put(`/api/products/${productId}/delivery-config/environment`, data),
  updateTemplate: (productId, data) => api.put(`/api/products/${productId}/delivery-config/templates`, data),
  
  // Template versioning
  publishTemplates: (productId, data) => api.post(`/api/products/${productId}/delivery-config/templates/publish`, data),
  rollbackTemplates: (productId, data) => api.post(`/api/products/${productId}/delivery-config/templates/rollback`, data),
  getTemplateHistory: (productId) => api.get(`/api/products/${productId}/delivery-config/templates/history`),
  
  // Presets
  applyPreset: (productId, data) => api.post(`/api/products/${productId}/delivery-config/preset`, data),
  
  // Preview MCP context
  getContext: (productId) => api.get(`/api/products/${productId}/delivery-config/context`),
  
  // Custom APIs / Integrations
  getIntegrations: (productId) => api.get(`/api/products/${productId}/delivery-config/integrations`),
  getIntegrationOptions: (productId) => api.get(`/api/products/${productId}/delivery-config/integrations/options`),
  createIntegration: (productId, data) => api.post(`/api/products/${productId}/delivery-config/integrations`, data),
  updateIntegration: (productId, apiId, data) => api.put(`/api/products/${productId}/delivery-config/integrations/${apiId}`, data),
  deleteIntegration: (productId, apiId) => api.delete(`/api/products/${productId}/delivery-config/integrations/${apiId}`),
};

// ============ MCP API Keys ============
export const mcpKeysApi = {
  // Check if config is complete for key generation
  getConfigStatus: (productId) => api.get(`/api/v1/mcp/keys/config-status?product_id=${productId}`),
  
  // API key management
  list: (productId) => api.get(`/api/v1/mcp/keys?product_id=${productId}`),
  create: (data) => api.post('/api/v1/mcp/keys', data),
  revoke: (keyId) => api.delete(`/api/v1/mcp/keys/${keyId}`),
  
  // Get config snippet for Claude Desktop
  getConfigSnippet: (keyPrefix) => api.get(`/api/v1/mcp/keys/config-snippet?key_prefix=${encodeURIComponent(keyPrefix)}`),
};

// ============ MCP Admin Test Harness API ============
export const mcpAdminTestApi = {
  // Dropdowns
  getOrganizations: () => api.get('/api/admin/mcp-test/organizations'),
  getProducts: (orgId) => api.get(`/api/admin/mcp-test/products/${orgId}`),
  getFeatures: (productId) => api.get(`/api/admin/mcp-test/features/${productId}`),
  getReleases: (productId) => api.get(`/api/admin/mcp-test/releases/${productId}`),
  getTeamMembersWithKeys: (productId) => api.get(`/api/admin/mcp-test/team-members/${productId}`),
  
  // Test endpoints (read-only)
  testFeaturesReady: (productId, releaseId = null, teamMemberId = null) => {
    let url = `/api/admin/mcp-test/test/features/ready?product_id=${productId}`;
    if (releaseId) url += `&release_id=${releaseId}`;
    if (teamMemberId) url += `&team_member_id=${teamMemberId}`;
    return api.get(url);
  },
  testContext: (productId, depth = 'standard', featureId = null) => {
    let url = `/api/admin/mcp-test/test/context?product_id=${productId}&depth=${depth}`;
    if (featureId) url += `&feature_id=${featureId}`;
    return api.get(url);
  },
  testActivity: (productId, limit = 50, featureId = null) => {
    let url = `/api/admin/mcp-test/test/activity?product_id=${productId}&limit=${limit}`;
    if (featureId) url += `&feature_id=${featureId}`;
    return api.get(url);
  },
  
  // Test endpoints (write operations)
  testLogDecision: (featureId, productId, data, testMode = true) => 
    api.post(`/api/admin/mcp-test/test/features/${featureId}/log?product_id=${productId}&test_mode=${testMode}`, data),
  testUpdateStatus: (featureId, productId, data, testMode = true) => 
    api.patch(`/api/admin/mcp-test/test/features/${featureId}/status?product_id=${productId}&test_mode=${testMode}`, data),
  testLinkCommit: (data, testMode = true) => 
    api.post(`/api/admin/mcp-test/test/commits/link?test_mode=${testMode}`, data),
};

// ============ API Keys ============
export const apiKeysApi = {
  // Admin operations (Organization Settings)
  listKeys: (productId) => api.get(`/api/products/${productId}/api-keys`),
  issueKey: (productId, data) => api.post(`/api/products/${productId}/api-keys`, data),
  updateKeyName: (productId, keyId, name) => api.patch(`/api/products/${productId}/api-keys/${keyId}/name`, { name }),
  reissueKey: (productId, keyId) => api.post(`/api/products/${productId}/api-keys/${keyId}/reissue`),
  deleteKey: (productId, keyId) => api.delete(`/api/products/${productId}/api-keys/${keyId}`),
  
  // Developer operations (Settings page)
  getMyKeys: () => api.get('/api/my-keys'),
  updateMyKeyName: (keyId, name) => api.patch(`/api/my-keys/${keyId}/name`, { name }),
  deleteMyKey: (keyId) => api.delete(`/api/my-keys/${keyId}`),
  requestKey: (productId, suggestedName) => api.post('/api/my-keys/request', { product_id: productId, suggested_name: suggestedName }),
  createKey: (productId, name) => api.post('/api/my-keys/create', { product_id: productId, name }),
  getAvailableProducts: () => api.get('/api/my-keys/available-products'),
  
  // Key requests (Admin)
  listKeyRequests: () => api.get('/api/api-keys/requests'),
  approveRequest: (requestId) => api.post(`/api/api-keys/requests/${requestId}/approve`),
  rejectRequest: (requestId, reason = null) => api.post(`/api/api-keys/requests/${requestId}/reject`, { reason }),
};

// ============ Stack Ranking API ============
export const stackRankingApi = {
  // === OWNER/MANAGEMENT Operations (require auth, use space_id) ===
  listSpaces: (productId) => api.get(`/api/stack-ranking/spaces?product_id=${productId}`),
  createSpace: (productId, data) => api.post(`/api/stack-ranking/spaces?product_id=${productId}`, data),
  getSpace: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}`),
  updateSpace: (spaceId, data) => api.put(`/api/stack-ranking/spaces/${spaceId}`, data),
  deleteSpace: (spaceId) => api.delete(`/api/stack-ranking/spaces/${spaceId}`),
  
  // Items (owner)
  listItems: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/items`),
  addItem: (spaceId, data) => api.post(`/api/stack-ranking/spaces/${spaceId}/items`, data),
  updateItem: (spaceId, itemId, data) => api.put(`/api/stack-ranking/spaces/${spaceId}/items/${itemId}`, data),
  deleteItem: (spaceId, itemId) => api.delete(`/api/stack-ranking/spaces/${spaceId}/items/${itemId}`),
  importItems: (spaceId, sourceType) => api.post(`/api/stack-ranking/spaces/${spaceId}/items/import?source_type=${sourceType}`),
  
  // Results & Participants (owner)
  getResults: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/results`),
  getParticipants: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/participants`),
  hasVoted: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/has-voted`),
  clearAllVotes: (spaceId) => api.delete(`/api/stack-ranking/spaces/${spaceId}/votes/all`),
  clearParticipantVotes: (spaceId, participantId) => api.delete(`/api/stack-ranking/spaces/${spaceId}/votes/${participantId}`),
  getTeamMembers: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/team-members`),
  sendToTeam: (spaceId, data) => api.post(`/api/stack-ranking/spaces/${spaceId}/send-to-team`, data),
  
  // Apply Rankings (owner)
  checkApplyRankings: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/apply-rankings-check`),
  applyRankings: (spaceId) => api.post(`/api/stack-ranking/spaces/${spaceId}/apply-rankings`),
  
  // AI Summarization (owner)
  summarizeResults: (spaceId) => api.post(`/api/stack-ranking/spaces/${spaceId}/summarize`),
  
  // Action Results (owner)
  getAvailableEpics: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/available-epics`),
  actionResults: (spaceId, data) => api.post(`/api/stack-ranking/spaces/${spaceId}/action-results`, data),
  
  // Suggestions Management (PM review)
  getSuggestions: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/suggestions`),
  handleSuggestion: (spaceId, suggestionId, action, epicId = null) => 
    api.post(`/api/stack-ranking/spaces/${spaceId}/suggestions/${suggestionId}/action`, { action, epic_id: epicId }),
  bulkHandleSuggestions: (spaceId, suggestionIds, action, epicId = null) =>
    api.post(`/api/stack-ranking/spaces/${spaceId}/suggestions/bulk-action`, { action, epic_id: epicId }, { params: { suggestion_ids: suggestionIds.join(',') } }),
  
  // Vote Summaries
  getSummaries: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/summaries`),
  getCurrentSummary: (spaceId) => api.get(`/api/stack-ranking/spaces/${spaceId}/summaries/current`),
  updateSummary: (spaceId, content) => api.put(`/api/stack-ranking/spaces/${spaceId}/summaries/current`, { content }),
  generateSummary: (spaceId) => api.post(`/api/stack-ranking/spaces/${spaceId}/summaries/generate`),
  
  // Source counts for wizard
  getSourceCounts: (productId) => api.get(`/api/stack-ranking/source-counts?product_id=${productId}`),
  
  // === VOTING Operations (use share_token, supports auth + guest) ===
  getVotingSpace: (shareToken) => api.get(`/api/stack-ranking/vote/${shareToken}`),
  getVotingItems: (shareToken) => api.get(`/api/stack-ranking/vote/${shareToken}/items`),
  getVoteStatus: (shareToken, email = null) => {
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    return api.get(`/api/stack-ranking/vote/${shareToken}/status${params}`);
  },
  submitVote: (shareToken, data) => api.post(`/api/stack-ranking/vote/${shareToken}/submit`, data),
  suggestItem: (shareToken, title, description, participantName) => 
    api.post(`/api/stack-ranking/vote/${shareToken}/suggest`, null, {
      params: { title, description, participant_name: participantName }
    }),
  
  // === LEGACY (for backwards compatibility - will be removed) ===
  getPublicSpace: (shareToken) => api.get(`/api/stack-ranking/vote/${shareToken}`),
  listPublicItems: (shareToken) => api.get(`/api/stack-ranking/vote/${shareToken}/items`),
  hasVotedPublic: (shareToken, email) => api.get(`/api/stack-ranking/vote/${shareToken}/status?email=${encodeURIComponent(email)}`),
  submitPublicVote: (shareToken, data) => api.post(`/api/stack-ranking/vote/${shareToken}/submit`, data),
};

// ============ Deep Research API (Legacy) ============
export const deepResearchApi = {
  marketAnalysis: (productId, focusArea = null, context = null) => 
    api.post('/api/research/market-analysis', { product_id: productId, focus_area: focusArea, context }),
  competitorIntel: (productId, focusArea = null, context = null) => 
    api.post('/api/research/competitor-intel', { product_id: productId, focus_area: focusArea, context }),
  userResearch: (productId, focusArea = null, context = null) => 
    api.post('/api/research/user-research', { product_id: productId, focus_area: focusArea, context }),
  techLandscape: (productId, focusArea = null, context = null) => 
    api.post('/api/research/tech-landscape', { product_id: productId, focus_area: focusArea, context }),
  getHistory: (productId) => api.get(`/api/research/history/${productId}`),
  getResult: (researchId) => api.get(`/api/research/${researchId}`),
  deleteResult: (researchId) => api.delete(`/api/research/${researchId}`),
};

// ============ Research Reports API (New) ============
export const researchReportsApi = {
  // CRUD
  create: (data) => api.post('/api/research/reports', data),
  list: (productId, researchType = null, status = null) => {
    let url = `/api/research/reports?product_id=${productId}`;
    if (researchType) url += `&research_type=${researchType}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  get: (reportId) => api.get(`/api/research/reports/${reportId}`),
  update: (reportId, data) => api.patch(`/api/research/reports/${reportId}`, data),
  delete: (reportId) => api.delete(`/api/research/reports/${reportId}`),
  
  // Research execution
  start: (reportId) => api.post(`/api/research/reports/${reportId}/start`),
  getStatus: (reportId) => api.get(`/api/research/reports/${reportId}/status`),
  cancel: (reportId) => api.post(`/api/research/reports/${reportId}/cancel`),
  retry: (reportId) => api.post(`/api/research/reports/${reportId}/retry`),
  
  // Report chat (follow-up questions) - Uses unified co-pilot with injected_context
  // The report content is passed as injected_context so the AI can reference it
  chat: (reportId, message, reportContent, reportTitle, conversationHistory = []) => 
    unifiedCopilotApi.chat('research_chat', reportId, {
      message,
      conversation_history: conversationHistory,
      // Frontend owns the label - backend blindly appends to context_data
      injected_context: `=== DEEP RESEARCH REPORT ===
Title: ${reportTitle || 'Research Report'}

${reportContent || 'No report content available.'}
=== END DEEP RESEARCH REPORT ===`
    }),
  
  // Export
  exportPdf: (reportId) => api.get(`/api/research/reports/${reportId}/export-pdf`, { responseType: 'blob' }),
};

// Token Management API
export const tokenApi = {
  getStatus: () => api.get('/api/tokens/status'),
  getTiers: () => api.get('/api/tokens/tiers'),
  getUsage: () => api.get('/api/tokens/usage'),
  getTopUser: () => api.get('/api/tokens/top-user'),
  getMyUsage: () => api.get('/api/tokens/my-usage'),
  previewBuyup: (tier) => api.post('/api/tokens/buyup/preview', { tier }),
  checkoutBuyup: (tier, successUrl, cancelUrl) => 
    api.post('/api/tokens/buyup/checkout', { tier, success_url: successUrl, cancel_url: cancelUrl }),
  reduceBuyup: (newTier) => api.post('/api/tokens/buyup/reduce', { new_tier: newTier }),
  cancelBuyup: () => api.post('/api/tokens/buyup/cancel'),
  // One-time top-up (free tier)
  checkoutTopup: (tier, successUrl, cancelUrl) =>
    api.post('/api/tokens/topup/checkout', { tier, success_url: successUrl, cancel_url: cancelUrl }),
  confirmTopup: (sessionId) =>
    api.post('/api/tokens/topup/confirm', { session_id: sessionId }),
  getTopupHistory: () => api.get('/api/tokens/topup/history'),
};

// ============ Email Settings API (Admin) ============
export const emailSettingsApi = {
  getSettings: () => api.get('/api/admin/email/settings'),
  updateSettings: (settings) => api.patch('/api/admin/email/settings', settings),
  getLogs: (limit = 20, after = null, before = null) => {
    const params = { limit };
    if (after) params.after = after;
    if (before) params.before = before;
    return api.get('/api/admin/email/logs', { params });
  },
  getEmailDetails: (emailId) => api.get(`/api/admin/email/logs/${emailId}`),
  resendEmail: (emailId) => api.post(`/api/admin/email/resend/${emailId}`),
  sendTestEmail: () => api.post('/api/admin/email/test'),
  // Email Templates
  getTemplates: () => api.get('/api/admin/email/templates'),
  getTemplate: (templateId) => api.get(`/api/admin/email/templates/${templateId}`),
  updateTemplate: (templateId, data) => api.patch(`/api/admin/email/templates/${templateId}`, data),
  sendTestTemplate: (templateId) => api.post(`/api/admin/email/templates/${templateId}/test`),
};

// ============ Dashboard Metrics API ============
export const dashboardMetricsApi = {
  getGoalMetrics: (productId = 'all') => api.get('/api/dashboard/metrics/goals', { params: { product_id: productId } }),
  getReleaseMetrics: (productId = 'all') => api.get('/api/dashboard/metrics/releases', { params: { product_id: productId } }),
  getRoadmapMetrics: (productId = 'all') => api.get('/api/dashboard/metrics/roadmap', { params: { product_id: productId } }),
  getUnstableGoals: (productId = 'all') => api.get('/api/dashboard/metrics/unstable-goals', { params: { product_id: productId } }),
  getNonCompliantKpis: (productId = 'all') => api.get('/api/dashboard/metrics/non-compliant-kpis', { params: { product_id: productId } }),
};

// ============ Requests API (Triage & Import) ============
export const requestsApi = {
  list: (productId, stage = null, type = null) => {
    const params = {};
    if (stage) params.stage = stage;
    if (type) params.type = type;
    return api.get(`/api/requests/product/${productId}`, { params });
  },
  get: (requestId) => api.get(`/api/requests/${requestId}`),
  create: (productId, data) => api.post(`/api/requests/product/${productId}`, data),
  update: (requestId, data) => api.patch(`/api/requests/${requestId}`, data),
  delete: (requestId) => api.delete(`/api/requests/${requestId}`),
  bulkUpdate: (requestIds, stage, considerationStatus = null) => 
    api.post('/api/requests/bulk-update', { 
      request_ids: requestIds, 
      stage, 
      consideration_status: considerationStatus 
    }),
  merge: (sourceRequestIds, targetRequestId) => 
    api.post('/api/requests/merge', { 
      source_request_ids: sourceRequestIds, 
      target_request_id: targetRequestId 
    }),
  import: (requestId, entityType, entityId) => 
    api.post(`/api/requests/${requestId}/import?entity_type=${entityType}&entity_id=${entityId}`),
  getStats: (productId) => api.get(`/api/requests/product/${productId}/stats`),
  // Bulk create from collaboration canvas harvest
  fromCollaboration: (productId, data) => 
    api.post(`/api/requests/product/${productId}/from-collaboration`, data),
};

// ============ Support Forms API (Customer Feedback) ============
export const supportFormsApi = {
  // Space Management
  create: (productId, data) => api.post(`/api/support-forms/spaces?product_id=${productId}`, data).then(r => r.data),
  list: (productId) => api.get(`/api/support-forms/spaces?product_id=${productId}`).then(r => r.data),
  get: (formId) => api.get(`/api/support-forms/spaces/${formId}`).then(r => r.data),
  update: (formId, data) => api.put(`/api/support-forms/spaces/${formId}`, data).then(r => r.data),
  delete: (formId) => api.delete(`/api/support-forms/spaces/${formId}`).then(r => r.data),
  regenerateToken: (formId) => api.post(`/api/support-forms/spaces/${formId}/regenerate-token`).then(r => r.data),
  
  // Submissions
  listSubmissions: (formId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type_filter', params.type);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    const query = queryParams.toString();
    return api.get(`/api/support-forms/spaces/${formId}/submissions${query ? `?${query}` : ''}`).then(r => r.data);
  },
  getSubmission: (submissionId) => api.get(`/api/support-forms/submissions/${submissionId}`).then(r => r.data),
  updateSubmissionStatus: (submissionId, status) => 
    api.put(`/api/support-forms/submissions/${submissionId}/status`, { status }).then(r => r.data),
  convertToRequest: (submissionId) => 
    api.post(`/api/support-forms/submissions/${submissionId}/convert`).then(r => r.data),
  
  // Public API (for embed)
  getPublicForm: (shareToken) => api.get(`/api/support-forms/public/${shareToken}`).then(r => r.data),
  submitPublicForm: (shareToken, data) => api.post(`/api/support-forms/public/${shareToken}/submit`, data).then(r => r.data),
  sendVerification: (shareToken, email) => 
    api.post(`/api/support-forms/public/${shareToken}/send-verification`, { email }).then(r => r.data),
  getEmbedCode: (shareToken) => api.get(`/api/support-forms/public/${shareToken}/embed-code`).then(r => r.data),
};

// ============ MCP Activity API ============
export const mcpActivityApi = {
  // Get activity feed for a product
  list: (productId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params.feature_id) queryParams.append('feature_id', params.feature_id);
    const query = queryParams.toString();
    return api.get(`/api/products/${productId}/activity${query ? `?${query}` : ''}`);
  },
  
  // Get aggregated stats for velocity metrics
  getStats: (productId, days = 7) => 
    api.get(`/api/products/${productId}/activity/stats?days=${days}`),
};

// ============ Delivery Dashboard API ============
export const deliveryDashboardApi = {
  getPipeline: (productId) =>
    api.get(`/api/products/${productId}/delivery-dashboard/pipeline`),
  getOpenPRs: (productId) =>
    api.get(`/api/products/${productId}/delivery-dashboard/open-prs`),
  getActivity: (productId, params = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.append('limit', params.limit);
    if (params.activity_type) q.append('activity_type', params.activity_type);
    if (params.feature_id) q.append('feature_id', params.feature_id);
    const qs = q.toString();
    return api.get(`/api/products/${productId}/delivery-dashboard/activity${qs ? `?${qs}` : ''}`);
  },
};

// ============ Role Settings API ============
export const roleSettingsApi = {
  // Get all role settings (admin only)
  list: () => api.get('/api/admin/roles'),
  // Get single role detail (admin only)
  get: (role) => api.get(`/api/admin/roles/${role}`),
  // Update a role's settings and permissions (admin only)
  update: (role, data) => api.put(`/api/admin/roles/${role}`, data),
  // Create a new custom role (admin only)
  create: (data) => api.post('/api/admin/roles', data),
  // Clone an existing role (admin only)
  clone: (role) => api.post(`/api/admin/roles/${role}/clone`),
  // Delete a custom role (admin only)
  delete: (role) => api.delete(`/api/admin/roles/${role}`),
  // Get default redirect URL for current user
  getDefaultRedirect: () => api.get('/api/auth/default-redirect'),
};

export const permissionsApi = {
  // Get current user's full permission set
  getMyPermissions: () => api.get('/api/permissions/me'),
  // Check a specific permission
  check: (obj, action, field = null) => api.post('/api/permissions/check', { object: obj, action, field }),
  // Get full permission matrix (admin only)
  getMatrix: () => api.get('/api/permissions/matrix'),
};

// ============ Drafts API ============
export const draftsApi = {
  // Delete a draft attachment
  deleteFile: (draftHash, attachmentId) => 
    api.delete(`/api/drafts/${draftHash}/${attachmentId}`),
  
  // List all attachments for a draft
  listAttachments: (draftHash) => 
    api.get(`/api/drafts/${draftHash}/attachments`),
  
  // Move draft files to product (on finalization)
  moveToProduct: (draftHash, productId) => 
    api.post(`/api/drafts/${draftHash}/move-to-product/${productId}`),
};

// ============ Product Wizard API ============
// MIGRATED CHAT TO UNIFIED API - Chat now routes through /api/copilot/wizard/{draft_hash}/chat
export const productWizardApi = {
  // Main chat endpoint - NOW USES UNIFIED ENDPOINT
  // Canvas state is passed via injected_context as stringified JSON (same pattern as Requests copilot)
  // This bypasses product validation since no product exists yet during wizard
  chat: (data) => unifiedCopilotApi.chat('wizard', data.draft_hash || 'new', {
    message: data.message,
    injected_context: data.canvas_state ? JSON.stringify(data.canvas_state) : null,
    attachments: data.attachments,
    conversation_history: data.conversation_history
  }),
  
  // Refresh S3 URLs for attachments
  refreshUrls: (draftHash, attachmentIds) => 
    api.post('/api/product-wizard/refresh-urls', { 
      draft_hash: draftHash, 
      attachment_ids: attachmentIds 
    }),
  
  // Get prompt info (for debugging)
  getPromptInfo: () => api.get('/api/product-wizard/prompt-info'),
  
  // Draft management
  saveDraft: (data) => api.post('/api/product-wizard/save-draft', data),
  getDraft: (draftHash) => api.get(`/api/product-wizard/draft/${draftHash}`),
  getDraftByProductId: (productId) => api.get(`/api/product-wizard/draft-by-product/${productId}`),
  listDrafts: () => api.get('/api/product-wizard/drafts'),
  deleteDraft: (draftHash) => api.delete(`/api/product-wizard/draft/${draftHash}`),
  
  // Create final product
  createProduct: (data) => api.post('/api/product-wizard/create-product', data),
  
  // URL Crawling - returns SSE stream, needs special handling
  // Use with fetch() directly, not axios
  getCrawlUrlEndpoint: () => `${API_BASE_URL}/api/product-wizard/crawl-url`,
};

// ============ Demo API ============
export const demoApi = {
  // Public endpoints (no auth required)
  getPublicConfig: () => api.get('/api/demo/config/public'),
  startDemo: (data) => api.post('/api/demo/start', data),
  
  // Admin endpoints
  getConfig: () => api.get('/api/demo/admin/config'),
  updateConfig: (data) => api.post('/api/demo/admin/config', data),
  getStats: () => api.get('/api/demo/admin/stats'),
  getLeads: (limit = 100, offset = 0) => 
    api.get(`/api/demo/admin/leads?limit=${limit}&offset=${offset}`),
};

// ============ Admin API ============
export const adminApi = {
  // Organizations (for demo management)
  getOrganizations: () => api.get('/api/admin/customers/organizations'),
  getOrganizationProducts: (orgId) => api.get(`/api/admin/customers/organizations/${orgId}/products`),
};

export default api;
